import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, BookOpen, Search, Star, Gift, BarChart3, HelpCircle, Menu, X, ShieldCheck } from "lucide-react";
import { Toaster } from "sonner";

const NAV = [
  { to: "/", label: "Início", icon: Home, testid: "nav-home" },
  { to: "/volumes", label: "Volumes", icon: BookOpen, testid: "nav-volumes" },
  { to: "/pesquisar", label: "Pesquisar", icon: Search, testid: "nav-search" },
  { to: "/favoritos", label: "Favoritos", icon: Star, testid: "nav-favorites" },
  { to: "/bonus", label: "Bônus", icon: Gift, testid: "nav-bonus" },
  { to: "/progresso", label: "Meu Progresso", icon: BarChart3, testid: "nav-progress" },
  { to: "/suporte", label: "Suporte", icon: HelpCircle, testid: "nav-support" },
];

const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
        <span className="text-xl">❤️</span>
      </div>
      <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-md bg-red-600 border-2 border-white" />
    </div>
    <div className="leading-tight">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">300 Atividades Práticas</div>
      <div className="text-sm font-bold text-slate-900">Biblioteca do Instrutor</div>
    </div>
  </div>
);

const NavItem = ({ to, label, icon: Icon, testid, onClick }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
        isActive
          ? "bg-blue-50 text-blue-700 border border-blue-100"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-transparent"
      }`
    }
  >
    <Icon className="w-4.5 h-4.5" size={18} />
    <span>{label}</span>
  </NavLink>
);

const Sidebar = ({ onNavigate }) => (
  <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-72 bg-white border-r border-slate-200 flex-col">
    <div className="p-6 border-b border-slate-100">
      <Brand />
    </div>
    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
      {NAV.map(item => <NavItem key={item.to} {...item} onClick={onNavigate} />)}
    </nav>
    <div className="p-4 border-t border-slate-100">
      <NavLink to="/admin" data-testid="nav-admin"
        className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors">
        <ShieldCheck size={14} /> Área do administrador
      </NavLink>
    </div>
  </aside>
);

const MobileHeader = ({ onOpen }) => (
  <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-4 h-14 flex items-center justify-between">
    <Brand />
    <button onClick={onOpen} data-testid="mobile-menu-open"
      className="p-2 rounded-lg hover:bg-slate-100 text-slate-700">
      <Menu size={22} />
    </button>
  </header>
);

const MobileDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  if (!open) return null;
  const go = () => onClose();
  return (
    <div className="lg:hidden fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl flex flex-col fade-up">
        <div className="p-5 flex items-center justify-between border-b border-slate-100">
          <Brand />
          <button onClick={onClose} data-testid="mobile-menu-close" className="p-2 rounded-lg hover:bg-slate-100">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map(item => <NavItem key={item.to} {...item} onClick={go} />)}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <button onClick={() => { navigate("/admin"); onClose(); }} data-testid="mobile-admin"
            className="w-full flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-800">
            <ShieldCheck size={14} /> Área do administrador
          </button>
        </div>
      </div>
    </div>
  );
};

const BottomNav = () => {
  const items = NAV.slice(0, 5);
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 grid grid-cols-5">
      {items.map(({ to, label, icon: Icon, testid }) => (
        <NavLink key={to} to={to} end={to === "/"} data-testid={`bottom-${testid}`}
          className={({ isActive }) =>
            `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium ${
              isActive ? "text-blue-600" : "text-slate-500"
            }`
          }
        >
          <Icon size={20} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

export const AppShell = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <MobileHeader onOpen={() => setDrawerOpen(true)} />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main className="lg:pl-72 pb-24 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          {children}
        </div>
      </main>
      <BottomNav />
      <Toaster position="top-center" richColors />
    </div>
  );
};
